import axios from 'axios';
import { getEnvVar } from '@/utils/env';

interface CalculatorSubmission {
  hiringData: {
    tech: HiringData;
    gtm: HiringData;
    gna: HiringData;
    exec: HiringData;
  };
  timelineMonths: number;
  rampPeriodMonths: number;
  recruitingTeam: {
    currentRecruiters: {
      tech: number;
      gtm: number;
      gna: number;
      exec: number;
    };
    fteHeadcount: number;
    outsourcedHeadcount: number;
  };
  recruitingGoals: {
    tech: number;
    gtm: number;
    gna: number;
    exec: number;
  };
  contactInfo: {
    name: string;
    email: string;
    company: string;
  };
}

interface HiringData {
  startingHeadcount: number;
  incrementalHires: number;
  backfillPercentage: number;
}

export const submitToHubSpot = async (data: CalculatorSubmission) => {
  try {
    const portalId = getEnvVar('VITE_HUBSPOT_PORTAL_ID');
    const formId = getEnvVar('VITE_HUBSPOT_FORM_ID');
    const accessToken = getEnvVar('VITE_HUBSPOT_ACCESS_TOKEN');

    if (!portalId || !formId || !accessToken) {
      throw new Error('Missing required HubSpot configuration');
    }

    // Create form fields array with all calculator data
    const fields = [
      { name: 'firstname', value: data.contactInfo.name },
      { name: 'email', value: data.contactInfo.email },
      { name: 'company', value: data.contactInfo.company },
      { name: 'timeline__months_', value: data.timelineMonths.toString() },
      { name: 'ramp__months_', value: data.rampPeriodMonths.toString() },
      { name: 'fte_headcount', value: data.recruitingTeam.fteHeadcount.toString() },
      { name: 'outsourced_headcount__', value: data.recruitingTeam.outsourcedHeadcount.toString() },
      // Tech & Product department
      { name: 'start_headcount__tech___product', value: data.hiringData.tech.startingHeadcount.toString() },
      { name: 'growth_headcount__tech___product', value: data.hiringData.tech.incrementalHires.toString() },
      { name: 'attrition__tech___product', value: data.hiringData.tech.backfillPercentage.toString() },
      { name: 'number_of_recruiters__tech___product', value: data.recruitingTeam.currentRecruiters.tech.toString() },
      { name: 'hiring_goals__tech___product', value: data.recruitingGoals.tech.toString() },
      // Go-to-market department
      { name: 'start_headcount__go_to_market', value: data.hiringData.gtm.startingHeadcount.toString() },
      { name: 'growth_headcount__go_to_market', value: data.hiringData.gtm.incrementalHires.toString() },
      { name: 'attrition__go_to_market', value: data.hiringData.gtm.backfillPercentage.toString() },
      { name: 'number_of_recruiters__go_to_market', value: data.recruitingTeam.currentRecruiters.gtm.toString() },
      { name: 'hiring_goals__go_to_market', value: data.recruitingGoals.gtm.toString() },
      // G&A department
      { name: 'start_headcount__g_a', value: data.hiringData.gna.startingHeadcount.toString() },
      { name: 'growth_headcount__g_a', value: data.hiringData.gna.incrementalHires.toString() },
      { name: 'attrition__g_a', value: data.hiringData.gna.backfillPercentage.toString() },
      { name: 'number_of_recruiters__g_a', value: data.recruitingTeam.currentRecruiters.gna.toString() },
      { name: 'hiring_goals__g_a', value: data.recruitingGoals.gna.toString() },
      // Executive department
      { name: 'start_headcount__executive', value: data.hiringData.exec.startingHeadcount.toString() },
      { name: 'growth_headcount__executive', value: data.hiringData.exec.incrementalHires.toString() },
      { name: 'attrition__executive', value: data.hiringData.exec.backfillPercentage.toString() },
      { name: 'number_of_recruiters__executive', value: data.recruitingTeam.currentRecruiters.exec.toString() },
      { name: 'hiring_goals__executive', value: data.recruitingGoals.exec.toString() },
    ];

    // Submit to HubSpot
    const response = await axios.post(
      `https://api.hsforms.com/submissions/v3/integration/submit/${portalId}/${formId}`,
      {
        fields: fields.map(field => ({
          name: field.name,
          value: field.value
        })),
        context: {
          hutk: document.cookie.match(/hubspotutk=(.*?)(;|$)/)?.[1] || undefined,
          pageUri: window.location.href,
          pageName: document.title
        },
        legalConsentOptions: {
          consent: {
            consentToProcess: true,
            text: "I agree to allow Talentful to store and process my personal data.",
            communications: [
              {
                value: true,
                subscriptionTypeId: 999,
                text: "I agree to receive marketing communications from Talentful"
              }
            ]
          }
        }
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    // Log the full error for debugging
    console.error('Error submitting to HubSpot:', error);
    
    // If it's an Axios error with a response, throw the HubSpot error message
    if (axios.isAxiosError(error) && error.response) {
      const hubspotError = error.response.data?.message || 'HubSpot submission failed';
      throw new Error(hubspotError);
    }
    
    // Otherwise, re-throw the original error
    throw error;
  }
};
